import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Divider,
  ButtonBase,
  Alert,
  Skeleton,
} from "@mui/material";
import { Calendar, RefreshCw } from "lucide-react";
import { CustomDateRangePopover } from "./components/CustomDateRangeDialog/CustomDateRangePopover";
import { DashboardKpiGrid } from "./components/Kpi";
import { SalesPipelineOverview } from "./components/SalesPipeline/SalesPipelineOverview";
import { PerformanceOverview } from "./components/PerformanceOverview/PerformanceOverview";
import { InventorySnapshot } from "./components/InventorySnapshot/InventorySnapshot";
import { LowValueCustomer } from "./components/LowValueCustomer/LowValueCustomer";
import { TimelyComparisonCharts } from "./components/TimelyComparisonCharts/TimelyComparisonCharts";
import { mergeSalesIndividualKpis } from "./data/salesIndividualKpis";
import { mergeServiceIndividualKpis } from "./data/serviceIndividualKpis";
import { mergeServiceOperationalInsights } from "./data/serviceOperationalInsights";
import { mergeSalesOperationalInsights } from "./data/salesOperationalInsights";
import { mergeSalesTimelyComparisonCharts } from "./data/salesTimelyComparisonCharts";
import { useSalesIndividualKpis } from "./hooks/useSalesIndividualKpis";
import { useServiceIndividualKpis } from "./hooks/useServiceIndividualKpis";
import { useServiceIndividualPerformance } from "./hooks/useServiceIndividualPerformance";
import { useServiceIndividualInventorySnapshot } from "./hooks/useServiceIndividualInventorySnapshot";
import { useServiceIndividualOperationalInsights } from "./hooks/useServiceIndividualOperationalInsights";
import { useServiceIndividualValueCustomers } from "./hooks/useServiceIndividualValueCustomers";
import { useSalesIndividualPerformance } from "./hooks/useSalesIndividualPerformance";
import { useSalesIndividualInventorySnapshot } from "./hooks/useSalesIndividualInventorySnapshot";
import { useSalesIndividualOperationalInsights } from "./hooks/useSalesIndividualOperationalInsights";
import { useSalesIndividualLowValueCustomers } from "./hooks/useSalesIndividualLowValueCustomers";
import { useSalesTimelyComparisonCharts } from "./hooks/useSalesTimelyComparisonCharts";
import { useServiceTimelyComparisonCharts } from "./hooks/useServiceTimelyComparisonCharts";
import { useSalesDashboardBootstrap } from "./hooks/useSalesDashboardBootstrap";
import { useServiceDashboardBootstrap } from "./hooks/useServiceDashboardBootstrap";
import { DashboardEnquirySidebar } from "./components/DashboardEnquirySidebar";
import { DashboardQuotationSidebar } from "./components/DashboardQuotationSidebar";
import { DashboardSalesOrderSidebar } from "./components/DashboardSalesOrderSidebar";
import { DashboardPurchaseOrderSidebar } from "./components/DashboardPurchaseOrderSidebar";
import { DashboardChallanSidebar } from "./components/DashboardChallanSidebar";
import { DashboardInventorySidebar } from "./components/DashboardInventorySidebar";
import { DashboardCustomerSidebar } from "./components/DashboardCustomerSidebar";
import { DashboardSectionDivider, DashboardSectionMotionProvider, useActivateDashboardSection } from "./components/DashboardSectionDivider";
import { dashboardPanelSx } from "./components/dashboardSurfaceStyles";
import { clearDashboardRequestCache } from "./services/dashboardRequestCache";

type DashboardTab = "sales" | "service";
type DashboardView = "individual" | "timely_comparison";

type IndividualPeriod =
  | "last_week"
  | "last_month"
  | "last_3_months"
  | "last_6_months"
  | "last_1_year"
  | "custom";

type ComparisonPeriod =
  | "weekly"
  | "monthly"
  | "quarterly"
  | "half_yearly"
  | "annually";

const VIEW_OPTIONS: { value: DashboardView; label: string }[] = [
  { value: "individual", label: "Individual" },
  { value: "timely_comparison", label: "Timely Comparison" },
];

const INDIVIDUAL_PERIODS: { value: IndividualPeriod; label: string }[] = [
  { value: "last_week", label: "Last Week" },
  { value: "last_month", label: "Last Month" },
  { value: "last_3_months", label: "Last 3 Months" },
  { value: "last_6_months", label: "Last 6 Months" },
  { value: "last_1_year", label: "Last 1 Year" },
  { value: "custom", label: "Custom Date" },
];

const COMPARISON_PERIODS: { value: ComparisonPeriod; label: string }[] = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "half_yearly", label: "Half-Yearly" },
  { value: "annually", label: "Annually" },
];

const pillButtonSx = (selected: boolean) => ({
  px: 1.25,
  py: 0.4,
  borderRadius: "999px",
  fontFamily: "'Poppins', sans-serif",
  fontSize: "11px",
  lineHeight: 1.2,
  fontWeight: selected ? 600 : 500,
  color: selected ? "#ffffff" : "#555555",
  backgroundColor: selected ? "#d2363a" : "transparent",
  transition: "all 0.2s ease",
  whiteSpace: "nowrap" as const,
  "&:hover": {
    backgroundColor: selected ? "#b92d31" : "#e5e7eb",
  },
});
const refreshButtonSx = {
  px: 1.25,
  py: 0.4,
  borderRadius: "999px",
  border: "1px solid #e5e7eb",
  fontFamily: "'Poppins', sans-serif",
  fontSize: "11px",
  lineHeight: 1.2,
  fontWeight: 500,
  color: "#555555",
  backgroundColor: "#ffffff",
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  transition: "all 0.2s ease",
  whiteSpace: "nowrap" as const,
  "&:hover": {
    backgroundColor: "#f3f4f6",
    borderColor: "#d1d5db",
  },
};
const SectionSkeleton = ({
  title,
  sectionId,
  rows = 4,
  columns = 4,
}: {
  title: string;
  sectionId: string;
  rows?: number;
  columns?: number;
}) => {
  const { activateSection } = useActivateDashboardSection();
  return (
  <Box sx={{ mt: 2.75 }} onClickCapture={() => activateSection(sectionId)}>
    <DashboardSectionDivider sectionId={sectionId} />
    <Box sx={{ mb: 1.25 }}>
      <Typography
        sx={{
          fontFamily: "'Poppins', sans-serif",
          fontSize: "14px",
          fontWeight: 600,
          color: "#111827",
        }}
      >
        {title}
      </Typography>
    </Box>
    <Box
      sx={{
        ...dashboardPanelSx,
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 1.5 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: `repeat(${Math.min(columns, 2)}, minmax(0, 1fr))`,
              md: `repeat(${columns}, minmax(0, 1fr))`,
            },
            gap: 1.25,
          }}
        >
          {Array.from({ length: rows }).map((_, index) => (
            <Box key={index}>
              <Skeleton variant="rounded" height={18} sx={{ mb: 0.8 }} />
              <Skeleton variant="rounded" height={36} />
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  </Box>
  );
};
const useViewportRequestGate = <TElement extends HTMLElement>(
  active: boolean,
  resetKey: string,
) => {
  const ref = useRef<TElement | null>(null);
  const [hasEnteredViewport, setHasEnteredViewport] = useState(false);

  useEffect(() => {
    setHasEnteredViewport(false);
  }, [resetKey]);

  useEffect(() => {
    if (!active || hasEnteredViewport) return;
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasEnteredViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px 0px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [active, hasEnteredViewport, resetKey]);

  return {
    ref,
    shouldRequest: active && hasEnteredViewport,
  };
};

export function OverviewDashboard() {
  const [activeTab, setActiveTab] = useState<DashboardTab>("sales");
  const [salesView, setSalesView] = useState<DashboardView>("individual");
  const [serviceView, setServiceView] =
    useState<DashboardView>("individual");

  const [salesIndividualPeriod, setSalesIndividualPeriod] =
    useState<IndividualPeriod>("last_month");
  const [serviceIndividualPeriod, setServiceIndividualPeriod] =
    useState<IndividualPeriod>("last_month");
  const [salesComparisonPeriod, setSalesComparisonPeriod] =
    useState<ComparisonPeriod>("monthly");
  const [serviceComparisonPeriod, setServiceComparisonPeriod] =
    useState<ComparisonPeriod>("monthly");

  const [customPickerOpen, setCustomPickerOpen] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [salesCustomLabel, setSalesCustomLabel] = useState("");
  const [serviceCustomLabel, setServiceCustomLabel] = useState("");
  const customDateAnchorRef = useRef<HTMLButtonElement | null>(null);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarType, setSidebarType] = useState<"product" | "service">("product");
  const [sidebarDateRange, setSidebarDateRange] = useState<{ from_date: string; to_date: string } | null>(null);
  const [sidebarKpiId, setSidebarKpiId] = useState<string>("new_enquiry");

  const [quotationSidebarOpen, setQuotationSidebarOpen] = useState(false);
  const [quotationSidebarType, setQuotationSidebarType] = useState<"product" | "service">("product");
  const [quotationSidebarDateRange, setQuotationSidebarDateRange] = useState<{ from_date: string; to_date: string } | null>(null);

  const [salesOrderSidebarOpen, setSalesOrderSidebarOpen] = useState(false);
  const [salesOrderSidebarType, setSalesOrderSidebarType] = useState<"product" | "service">("product");
  const [salesOrderSidebarDateRange, setSalesOrderSidebarDateRange] = useState<{ from_date: string; to_date: string } | null>(null);

  const [purchaseOrderSidebarOpen, setPurchaseOrderSidebarOpen] = useState(false);
  const [purchaseOrderSidebarType, setPurchaseOrderSidebarType] = useState<"product" | "service">("product");
  const [purchaseOrderSidebarDateRange, setPurchaseOrderSidebarDateRange] = useState<{ from_date: string; to_date: string } | null>(null);

  const [challanSidebarOpen, setChallanSidebarOpen] = useState(false);
  const [challanSidebarType, setChallanSidebarType] = useState<"product" | "service">("product");
  const [challanSidebarDateRange, setChallanSidebarDateRange] = useState<{ from_date: string; to_date: string } | null>(null);

  const [inventorySidebarOpen, setInventorySidebarOpen] = useState(false);
  const [inventoryStockType, setInventoryStockType] = useState<"out_of_stock" | "low_stock" | null>(null);

  const [customerSidebarOpen, setCustomerSidebarOpen] = useState(false);
  const [customerSidebarType, setCustomerSidebarType] = useState<"product" | "service">("product");
  const [customerSidebarDateRange, setCustomerSidebarDateRange] = useState<{ from_date: string; to_date: string } | null>(null);
  const [customerSidebarKpiId, setCustomerSidebarKpiId] = useState<"new_customer_added" | "customer_inactive_over_1_year" | "service_customer_inactive_over_1_year">("new_customer_added");

  const [dashboardRefreshKey, setDashboardRefreshKey] = useState(0);

  const handleInventoryCardClick = (cardId: string) => {
    if (cardId === "out_of_stock" || cardId === "low_stock" || cardId === "incoming_stock") {
      setInventoryStockType(cardId as any);
      setInventorySidebarOpen(true);
    }
  };

  const activeView = activeTab === "sales" ? salesView : serviceView;
  const setActiveView =
    activeTab === "sales" ? setSalesView : setServiceView;

  const individualPeriod =
    activeTab === "sales" ? salesIndividualPeriod : serviceIndividualPeriod;
  const setIndividualPeriod =
    activeTab === "sales"
      ? setSalesIndividualPeriod
      : setServiceIndividualPeriod;

  const comparisonPeriod =
    activeTab === "sales" ? salesComparisonPeriod : serviceComparisonPeriod;
  const setComparisonPeriod =
    activeTab === "sales"
      ? setSalesComparisonPeriod
      : setServiceComparisonPeriod;

  const customLabel =
    activeTab === "sales" ? salesCustomLabel : serviceCustomLabel;
  const setCustomLabel =
    activeTab === "sales" ? setSalesCustomLabel : setServiceCustomLabel;

  const sectionMotionKey = useMemo(() => {
    if (activeView === "timely_comparison") {
      return [
        activeTab,
        activeView,
        comparisonPeriod,
      ].join("|");
    }
    return [
      activeTab,
      activeView,
      individualPeriod,
      individualPeriod === "custom" ? customStartDate : "",
      individualPeriod === "custom" ? customEndDate : "",
    ].join("|");
  }, [
    activeTab,
    activeView,
    comparisonPeriod,
    individualPeriod,
    customStartDate,
    customEndDate,
  ]);

  const mapComparisonToIndividualPeriod = (
    period: string,
  ): "last_week" | "last_month" | "last_3_months" | "last_6_months" | "last_1_year" => {
    switch (period) {
      case "weekly":
        return "last_week";
      case "monthly":
        return "last_month";
      case "quarterly":
        return "last_3_months";
      case "half_yearly":
        return "last_6_months";
      case "annually":
        return "last_1_year";
      default:
        return "last_month";
    }
  };

  const salesIndividualFilters = useMemo(
    () => {
      if (salesView === "timely_comparison") {
        return { period: mapComparisonToIndividualPeriod(salesComparisonPeriod) };
      }
      return {
        period: salesIndividualPeriod,
        from_date: salesIndividualPeriod === "custom" ? customStartDate : undefined,
        to_date: salesIndividualPeriod === "custom" ? customEndDate : undefined,
      };
    },
    [salesView, salesComparisonPeriod, salesIndividualPeriod, customStartDate, customEndDate],
  );

  const serviceIndividualFilters = useMemo(
    () => {
      if (serviceView === "timely_comparison") {
        return { period: mapComparisonToIndividualPeriod(serviceComparisonPeriod) };
      }
      return {
        period: serviceIndividualPeriod,
        from_date: serviceIndividualPeriod === "custom" ? customStartDate : undefined,
        to_date: serviceIndividualPeriod === "custom" ? customEndDate : undefined,
      };
    },
    [serviceView, serviceComparisonPeriod, serviceIndividualPeriod, customStartDate, customEndDate],
  );

  const isSalesIndividualViewActive =
    activeTab === "sales" && salesView === "individual";
  const isServiceIndividualViewActive =
    activeTab === "service" && serviceView === "individual";
  const isSalesTimelyActive =
    activeTab === "sales" && salesView === "timely_comparison";
  const isServiceTimelyActive =
    activeTab === "service" && serviceView === "timely_comparison";

  const salesIndividualFilterKey = [
    salesIndividualFilters.period,
    salesIndividualFilters.from_date || "",
    salesIndividualFilters.to_date || "",
  ].join("|");
  const serviceIndividualFilterKey = [
    serviceIndividualFilters.period,
    serviceIndividualFilters.from_date || "",
    serviceIndividualFilters.to_date || "",
  ].join("|");

  const salesTimelyIndividualGate = useViewportRequestGate<HTMLDivElement>(
    isSalesTimelyActive,
    `sales-timely-individual|${salesIndividualFilterKey}`,
  );
  const serviceTimelyIndividualGate = useViewportRequestGate<HTMLDivElement>(
    isServiceTimelyActive,
    `service-timely-individual|${serviceIndividualFilterKey}`,
  );
  const salesInventoryGate = useViewportRequestGate<HTMLDivElement>(
    activeTab === "sales",
    `sales-inventory|${salesIndividualFilterKey}`,
  );
  const salesLowValueGate = useViewportRequestGate<HTMLDivElement>(
    activeTab === "sales",
    `sales-low-value|${salesIndividualFilterKey}`,
  );
  const serviceInventoryGate = useViewportRequestGate<HTMLDivElement>(
    activeTab === "service",
    `service-inventory|${serviceIndividualFilterKey}`,
  );
  const serviceValueCustomersGate = useViewportRequestGate<HTMLDivElement>(
    activeTab === "service",
    `service-value-customers|${serviceIndividualFilterKey}`,
  );

  const isSalesIndividualActive =
    isSalesIndividualViewActive || salesTimelyIndividualGate.shouldRequest;
  const isServiceIndividualActive =
    isServiceIndividualViewActive || serviceTimelyIndividualGate.shouldRequest;

  const salesBootstrapState = useSalesDashboardBootstrap(
    salesIndividualFilters,
    isSalesIndividualViewActive,
    salesView,
    salesComparisonPeriod,
    dashboardRefreshKey,
  );

  const serviceBootstrapState = useServiceDashboardBootstrap(
    serviceIndividualFilters,
    isServiceIndividualViewActive,
    serviceView,
    serviceComparisonPeriod,
    dashboardRefreshKey,
  );

  const salesKpiFallbackState = useSalesIndividualKpis(
    salesIndividualFilters,
    isSalesIndividualActive && (!isSalesIndividualViewActive || !!salesBootstrapState.error),
    dashboardRefreshKey,
  );

  const serviceKpiFallbackState = useServiceIndividualKpis(
    serviceIndividualFilters,
    isServiceIndividualActive && (!isServiceIndividualViewActive || !!serviceBootstrapState.error),
    dashboardRefreshKey,
  );

  const salesPerformanceFallbackState = useSalesIndividualPerformance(
    salesIndividualFilters,
    isSalesIndividualActive && (!isSalesIndividualViewActive || !!salesBootstrapState.error),
    dashboardRefreshKey,
  );

  const servicePerformanceFallbackState = useServiceIndividualPerformance(
    serviceIndividualFilters,
    isServiceIndividualActive && (!isServiceIndividualViewActive || !!serviceBootstrapState.error),
    dashboardRefreshKey,
  );

  const salesInsightsFallbackState = useSalesIndividualOperationalInsights(
    salesIndividualFilters,
    isSalesIndividualActive && (!isSalesIndividualViewActive || !!salesBootstrapState.error),
    dashboardRefreshKey,
  );

  const serviceInsightsFallbackState = useServiceIndividualOperationalInsights(
    serviceIndividualFilters,
    isServiceIndividualActive && (!isServiceIndividualViewActive || !!serviceBootstrapState.error),
    dashboardRefreshKey,
  );

  const salesInventoryFallbackState = useSalesIndividualInventorySnapshot(
    salesIndividualFilters,
    isSalesIndividualActive && salesInventoryGate.shouldRequest && (!isSalesIndividualViewActive || !salesBootstrapState.data?.inventory),
    dashboardRefreshKey,
  );

  const salesLowValueCustomersFallbackState = useSalesIndividualLowValueCustomers(
    salesIndividualFilters,
    isSalesIndividualActive && salesLowValueGate.shouldRequest && (!isSalesIndividualViewActive || !salesBootstrapState.data?.lowValueCustomers),
    dashboardRefreshKey,
  );

  const serviceInventoryFallbackState = useServiceIndividualInventorySnapshot(
    serviceIndividualFilters,
    isServiceIndividualActive && serviceInventoryGate.shouldRequest && (!isServiceIndividualViewActive || !serviceBootstrapState.data?.inventory),
    dashboardRefreshKey,
  );

  const serviceValueCustomersFallbackState = useServiceIndividualValueCustomers(
    serviceIndividualFilters,
    isServiceIndividualActive && serviceValueCustomersGate.shouldRequest && (!isServiceIndividualViewActive || !serviceBootstrapState.data?.valueCustomers),
    dashboardRefreshKey,
  );

  const salesTimelyChartsState = useSalesTimelyComparisonCharts(
    salesComparisonPeriod,
    isSalesTimelyActive,
    dashboardRefreshKey,
  );

  const serviceTimelyChartsState = useServiceTimelyComparisonCharts(
    serviceComparisonPeriod,
    isServiceTimelyActive,
    dashboardRefreshKey,
  );

  const salesKpiState = salesBootstrapState.data
    ? { data: salesBootstrapState.data.kpis, isLoading: salesBootstrapState.isLoading, error: salesBootstrapState.error }
    : salesKpiFallbackState;
  const salesPerformanceState = salesBootstrapState.data
    ? { data: salesBootstrapState.data.performance, isLoading: salesBootstrapState.isLoading, error: salesBootstrapState.error }
    : salesPerformanceFallbackState;
  const salesInsightsState = salesBootstrapState.data
    ? { data: salesBootstrapState.data.operationalInsights, isLoading: salesBootstrapState.isLoading, error: salesBootstrapState.error }
    : salesInsightsFallbackState;
  const salesInventoryState = salesBootstrapState.data?.inventory
    ? { data: salesBootstrapState.data.inventory, isLoading: salesBootstrapState.isLoading, error: salesBootstrapState.error }
    : salesInventoryFallbackState;
  const salesLowValueCustomersState = salesBootstrapState.data?.lowValueCustomers
    ? { data: salesBootstrapState.data.lowValueCustomers, isLoading: salesBootstrapState.isLoading, error: salesBootstrapState.error }
    : salesLowValueCustomersFallbackState;

  const serviceKpiState = serviceBootstrapState.data
    ? { data: serviceBootstrapState.data.kpis, isLoading: serviceBootstrapState.isLoading, error: serviceBootstrapState.error }
    : serviceKpiFallbackState;
  const servicePerformanceState = serviceBootstrapState.data
    ? { data: serviceBootstrapState.data.performance, isLoading: serviceBootstrapState.isLoading, error: serviceBootstrapState.error }
    : servicePerformanceFallbackState;
  const serviceInsightsState = serviceBootstrapState.data
    ? { data: serviceBootstrapState.data.operationalInsights, isLoading: serviceBootstrapState.isLoading, error: serviceBootstrapState.error }
    : serviceInsightsFallbackState;
  const serviceInventoryState = serviceBootstrapState.data?.inventory
    ? { data: serviceBootstrapState.data.inventory, isLoading: serviceBootstrapState.isLoading, error: serviceBootstrapState.error }
    : serviceInventoryFallbackState;
  const serviceValueCustomersState = serviceBootstrapState.data?.valueCustomers
    ? { data: serviceBootstrapState.data.valueCustomers, isLoading: serviceBootstrapState.isLoading, error: serviceBootstrapState.error }
    : serviceValueCustomersFallbackState;

  const salesKpiItems = useMemo(
    () => mergeSalesIndividualKpis(salesKpiState.data?.kpis),
    [salesKpiState.data?.kpis],
  );

  const serviceKpiItems = useMemo(
    () => mergeServiceIndividualKpis(serviceKpiState.data?.kpis),
    [serviceKpiState.data?.kpis],
  );

  const serviceOperationalInsightsItems = useMemo(
    () => mergeServiceOperationalInsights(serviceInsightsState.data?.items),
    [serviceInsightsState.data?.items],
  );

  const salesOperationalInsightsItems = useMemo(
    () => mergeSalesOperationalInsights(salesInsightsState.data?.items),
    [salesInsightsState.data?.items],
  );

  const salesTimelyChartSeries = useMemo(
    () => mergeSalesTimelyComparisonCharts(salesTimelyChartsState.data?.series),
    [salesTimelyChartsState.data?.series],
  );

  const serviceTimelyChartSeries = useMemo(
    () => mergeSalesTimelyComparisonCharts(serviceTimelyChartsState.data?.series),
    [serviceTimelyChartsState.data?.series],
  );

  const handleDashboardRefresh = () => {
    clearDashboardRequestCache();
    setDashboardRefreshKey((current) => current + 1);
  };

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: number,
  ) => {
    setActiveTab(newValue === 0 ? "sales" : "service");
  };

  const handleIndividualPeriodClick = (period: IndividualPeriod) => {
    if (period === "custom") {
      setCustomPickerOpen(true);
      return;
    }
    setCustomPickerOpen(false);
    setIndividualPeriod(period);
  };

  const handleCustomDateApply = () => {
    if (!customStartDate || !customEndDate) return;
    setIndividualPeriod("custom");
    setCustomLabel(`${customStartDate} to ${customEndDate}`);
    setCustomPickerOpen(false);
  };

  const handleCustomDateClear = () => {
    setCustomStartDate("");
    setCustomEndDate("");
    setCustomLabel("");
    if (individualPeriod === "custom") {
      setIndividualPeriod("last_month");
    }
  };

  const handleCardClick = (itemId: string, type: "product" | "service") => {
    if (
      itemId === "total_sales_value" ||
      itemId === "total_service_value" ||
      itemId === "avg_dispatch_lead_time" ||
      itemId === "avg_job_completion_lead_time"
    ) return;

    if (itemId === "new_customer_added" || itemId === "customer_inactive_over_1_year" || itemId === "service_customer_inactive_over_1_year") {
      setCustomerSidebarType(type);
      setCustomerSidebarKpiId(itemId as any);
      const insightsRange = type === "product"
        ? salesInsightsState.data?.date_range
        : serviceInsightsState.data?.date_range;
      const fallbackRange = type === "product"
        ? salesKpiState.data?.date_range
        : serviceKpiState.data?.date_range;
      const targetRange = insightsRange || fallbackRange;
      if (targetRange) {
        setCustomerSidebarDateRange({
          from_date: targetRange.from_date,
          to_date: targetRange.to_date,
        });
        setCustomerSidebarOpen(true);
      }
      return;
    }

    if (itemId === "low_stock_items") {
      handleInventoryCardClick("low_stock");
      return;
    }

    if (itemId === "jobs_pending_completion") {
      const insightsRange = serviceInsightsState.data?.date_range;
      const fallbackRange = serviceKpiState.data?.date_range;
      const targetRange = insightsRange || fallbackRange;
      if (targetRange) {
        setSidebarKpiId("jobs_pending_completion");
        setSalesOrderSidebarType("service");
        setSalesOrderSidebarDateRange({
          from_date: targetRange.from_date,
          to_date: targetRange.to_date,
        });
        setSalesOrderSidebarOpen(true);
      }
      return;
    }

    let kpiIdToUse = itemId;
    const isPipelineClick = itemId.startsWith("pipeline_");
    const rawStage = itemId.replace("pipeline_", "");

    if (rawStage === "order_own") {
      kpiIdToUse = isPipelineClick
        ? "pipeline_order_own"
        : (type === "product" ? "orders_received" : "service_order_received");
    } else if (rawStage === "dispatched") {
      kpiIdToUse = isPipelineClick
        ? (type === "product" ? "pipeline_orders_dispatched" : "pipeline_challan_created")
        : (type === "product" ? "orders_dispatched" : "challan_created");
    } else if (rawStage === "enquiry_received") {
      kpiIdToUse = isPipelineClick
        ? (type === "product" ? "pipeline_enquiries_received" : "pipeline_service_enquiry_received")
        : (type === "product" ? "enquiries_received" : "service_enquiry_received");
    } else if (rawStage === "quotation_created") {
      kpiIdToUse = isPipelineClick
        ? (type === "product" ? "pipeline_quotation_created" : "pipeline_service_quotation_created")
        : (type === "product" ? "quotation_created" : "service_quotation_created");
    } else if (rawStage === "quotation_sent") {
      kpiIdToUse = isPipelineClick
        ? (type === "product" ? "pipeline_quotation_sent" : "pipeline_service_quotation_sent")
        : (type === "product" ? "quotation_sent" : "service_quotation_sent");
    } else if (rawStage === "quotation_won") {
      kpiIdToUse = isPipelineClick
        ? (type === "product" ? "pipeline_quotation_won" : "pipeline_service_quotation_won")
        : (type === "product" ? "quotation_won" : "service_quotation_won");
    } else if (rawStage === "quotation_lost") {
      kpiIdToUse = isPipelineClick
        ? (type === "product" ? "pipeline_quotation_lost" : "pipeline_service_quotation_lost")
        : (type === "product" ? "quotation_lost" : "service_quotation_lost");
    }

    const dateRange =
      type === "product"
        ? salesKpiState.data?.date_range
        : serviceKpiState.data?.date_range;

    if (dateRange) {
      setSidebarKpiId(kpiIdToUse);
      const parsedRange = {
        from_date: dateRange.from_date,
        to_date: dateRange.to_date,
      };

      const baseKpi = kpiIdToUse.replace("pipeline_", "");

      if (
        [
          "quotation_created",
          "service_quotation_created",
          "quotation_won",
          "service_quotation_won",
          "quotation_lost",
          "service_quotation_lost",
          "quotation_sent",
          "service_quotation_sent",
        ].includes(baseKpi)
      ) {
        setQuotationSidebarType(type);
        setQuotationSidebarDateRange(parsedRange);
        setQuotationSidebarOpen(true);
      } else if (
        [
          "orders_received",
          "service_order_received",
          "orders_packed",
          "orders_dispatched",
          "job_completed",
          "order_received",
          "order_own",
          "dispatched",
        ].includes(baseKpi)
      ) {
        if (baseKpi === "orders_dispatched" && type === "service") {
          setSidebarKpiId(kpiIdToUse.startsWith("pipeline_") ? "pipeline_challan_created" : "challan_created");
          setChallanSidebarType(type);
          setChallanSidebarDateRange(parsedRange);
          setChallanSidebarOpen(true);
        } else {
          setSalesOrderSidebarType(type);
          setSalesOrderSidebarDateRange(parsedRange);
          setSalesOrderSidebarOpen(true);
        }
      } else if (
        ["po_raised", "po_pending"].includes(baseKpi)
      ) {
        setPurchaseOrderSidebarType(type);
        setPurchaseOrderSidebarDateRange(parsedRange);
        setPurchaseOrderSidebarOpen(true);
      } else if (
        ["challan_created", "accessories_used", "accessories_returned"].includes(baseKpi)
      ) {
        setChallanSidebarType(type);
        setChallanSidebarDateRange(parsedRange);
        setChallanSidebarOpen(true);
      } else {
        // Enquiries (including enquiry_received / service_enquiry_received)
        setSidebarType(type);
        setSidebarDateRange(parsedRange);
        setSidebarOpen(true);
      }
    }
  };

  return (
    <div
      className="page-content"
      style={{
        backgroundColor: "#e9ecef",
        minHeight: "100vh",
        paddingLeft: 6,
        paddingRight: 6,
        paddingBottom: 8,
        paddingTop: "calc(var(--vz-header-height, 56px) + 4px)",
      }}
    >
      <div
        style={{
          backgroundColor: "#fff",
          padding: "10px 12px",
          borderRadius: "10px",
          boxShadow: "0 0 15px rgba(0,0,0,0.1)",
        }}
      >
        <DashboardSectionMotionProvider animationKey={sectionMotionKey}>
        <Box>
          <Typography
            variant="h5"
            component="h1"
            fontWeight="600"
            color="#111827"
            sx={{ fontSize: "18px", lineHeight: 1.3 }}
          >
            Dashboard Overview
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: "12px" }}>
            Monitor your business performance in real-time
          </Typography>
        </Box>

        <div className="mecord-tabs-wrap" style={{ marginTop: "12px" }}>
          <Box sx={{ width: "100%" }}>
            <Tabs
              value={activeTab === "sales" ? 0 : 1}
              onChange={handleTabChange}
              sx={{
                minHeight: 30,
                "& .MuiTab-root": {
                  fontFamily: "'Poppins', sans-serif",
                  textTransform: "none",
                  color: "#555555",
                  minHeight: 30,
                  paddingTop: "2px",
                  paddingBottom: "2px",
                  transition: "color 0.2s ease",
                },
                "& .MuiTab-root:hover": {
                  color: "#d2363a",
                },
                "& .Mui-selected": {
                  color: "#d2363a !important",
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#d2363a",
                  height: "3px",
                  transition: "all 0.3s ease",
                },
              }}
            >
              <Tab label="Sales" />
              <Tab label="Service" />
            </Tabs>
            <Divider sx={{ mt: 0, borderColor: "#c35f61" }} />
          </Box>
        </div>

        <Box
          sx={{
            mt: 1.5,
            display: "inline-flex",
            alignItems: "center",
            p: "2px",
            borderRadius: "999px",
            backgroundColor: "#f3f4f6",
            border: "1px solid #e5e7eb",
            gap: "2px",
          }}
        >
          {VIEW_OPTIONS.map((option) => {
            const selected = activeView === option.value;
            return (
              <ButtonBase
                key={option.value}
                onClick={() => setActiveView(option.value)}
                sx={pillButtonSx(selected)}
              >
                {option.label}
              </ButtonBase>
            );
          })}
        </Box>

        {activeView === "individual" ? (
          <Box
            sx={{
              mt: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 0.75,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.75,
              }}
            >
              {INDIVIDUAL_PERIODS.filter((o) => o.value !== "custom").map(
                (option) => {
                  const selected = individualPeriod === option.value;
                  return (
                    <ButtonBase
                      key={option.value}
                      onClick={() => handleIndividualPeriodClick(option.value)}
                      sx={{
                        ...pillButtonSx(selected),
                        border: selected ? "none" : "1px solid #e5e7eb",
                        backgroundColor: selected ? "#d2363a" : "#ffffff",
                      }}
                    >
                      {option.label}
                    </ButtonBase>
                  );
                },
              )}
            </Box>

            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
              <ButtonBase
                ref={customDateAnchorRef}
                onClick={() => handleIndividualPeriodClick("custom")}
                sx={{
                  ...pillButtonSx(individualPeriod === "custom"),
                  border:
                    individualPeriod === "custom" ? "none" : "1px solid #e5e7eb",
                  backgroundColor:
                    individualPeriod === "custom" ? "#d2363a" : "#ffffff",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Calendar size={12} />
                {individualPeriod === "custom" && customLabel
                  ? customLabel
                  : "Custom Date"}
              </ButtonBase>

              <ButtonBase
                onClick={handleDashboardRefresh}
                aria-label="Refresh dashboard"
                sx={refreshButtonSx}
              >
                <RefreshCw size={12} />
                Refresh
              </ButtonBase>
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              mt: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 0.75,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.75,
              }}
            >
              {COMPARISON_PERIODS.map((option) => {
                const selected = comparisonPeriod === option.value;
                return (
                  <ButtonBase
                    key={option.value}
                    onClick={() => setComparisonPeriod(option.value)}
                    sx={{
                      ...pillButtonSx(selected),
                      border: selected ? "none" : "1px solid #e5e7eb",
                      backgroundColor: selected ? "#d2363a" : "#ffffff",
                    }}
                  >
                    {option.label}
                  </ButtonBase>
                );
              })}
            </Box>

            <ButtonBase
              onClick={handleDashboardRefresh}
              aria-label="Refresh dashboard"
              sx={refreshButtonSx}
            >
              <RefreshCw size={12} />
              Refresh
            </ButtonBase>
          </Box>
        )}

        <CustomDateRangePopover
          open={customPickerOpen}
          anchorEl={customDateAnchorRef.current}
          startDate={customStartDate}
          endDate={customEndDate}
          onClose={() => setCustomPickerOpen(false)}
          onStartDateChange={setCustomStartDate}
          onEndDateChange={setCustomEndDate}
          onApply={handleCustomDateApply}
          onClear={handleCustomDateClear}
        />

        {activeTab === "sales" && activeView === "individual" ? (
          <>
            {(salesKpiState.error ||
              salesPerformanceState.error ||
              salesInventoryState.error ||
              salesInsightsState.error ||
              salesLowValueCustomersState.error) ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {salesKpiState.error ||
                  salesPerformanceState.error ||
                  salesInventoryState.error ||
                  salesInsightsState.error ||
                  salesLowValueCustomersState.error}
              </Alert>
            ) : null}
            <DashboardKpiGrid
              title="Sales KPIs"
              subtitle="Product sales pipeline metrics for the selected period"
              items={salesKpiItems}
              columns={{ xs: 1, sm: 2, md: 5, lg: 5 }}
              onCardClick={(itemId) => handleCardClick(itemId, "product")}
              isLoading={salesKpiState.isLoading}
              sectionId="kpi"
            />
            {salesKpiState.data?.pipeline_stages ? (
              <SalesPipelineOverview
                data={{ stages: salesKpiState.data.pipeline_stages }}
                onStageClick={(stageId) => handleCardClick("pipeline_" + stageId, "product")}
              />
            ) : salesKpiState.isLoading ? (
              <SectionSkeleton title="Sales Pipeline Overview" sectionId="pipeline" rows={1} columns={5} />
            ) : null}
            {salesPerformanceState.data ? (
              <PerformanceOverview data={salesPerformanceState.data} />
            ) : salesPerformanceState.isLoading ? (
              <SectionSkeleton title="Performance Overview" sectionId="performance" rows={5} columns={4} />
            ) : null}
            <Box ref={salesInventoryGate.ref} sx={{ minHeight: salesInventoryState.data ? 0 : 24 }}>
              {salesInventoryState.data ? (
                <InventorySnapshot
                  data={salesInventoryState.data}
                  onCardClick={handleInventoryCardClick}
                />
              ) : salesInventoryState.isLoading || !salesInventoryGate.shouldRequest ? (
                <SectionSkeleton title="Inventory Snapshot" sectionId="inventory" rows={3} columns={3} />
              ) : null}
            </Box>
            <DashboardKpiGrid
              title="Key Operational Insights"
              subtitle="Operational health metrics for the selected period"
              items={salesOperationalInsightsItems}
              columns={{ xs: 1, sm: 2, md: 4, lg: 4 }}
              isLoading={salesInsightsState.isLoading}
              onCardClick={(itemId) => handleCardClick(itemId, "product")}
              sectionId="insights"
            />
            <Box ref={salesLowValueGate.ref} sx={{ minHeight: salesLowValueCustomersState.data ? 0 : 24 }}>
              {salesLowValueCustomersState.data ? (
                <LowValueCustomer data={salesLowValueCustomersState.data} />
              ) : salesLowValueCustomersState.isLoading || !salesLowValueGate.shouldRequest ? (
                <SectionSkeleton title="Low Value Customer" sectionId="low-value" rows={5} columns={5} />
              ) : null}
            </Box>
          </>
        ) : null}

        {activeTab === "service" && activeView === "individual" ? (
          <>
            {(serviceKpiState.error ||
              servicePerformanceState.error ||
              serviceInventoryState.error ||
              serviceInsightsState.error ||
              serviceValueCustomersState.error) ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {serviceKpiState.error ||
                  servicePerformanceState.error ||
                  serviceInventoryState.error ||
                  serviceInsightsState.error ||
                  serviceValueCustomersState.error}
              </Alert>
            ) : null}
            <DashboardKpiGrid
              title="Service KPIs"
              subtitle="Service pipeline metrics for the selected period"
              items={serviceKpiItems}
              columns={{ xs: 1, sm: 2, md: 5, lg: 5 }}
              onCardClick={(itemId) => handleCardClick(itemId, "service")}
              isLoading={serviceKpiState.isLoading}
              sectionId="kpi"
            />
            {serviceKpiState.data?.pipeline_stages ? (
              <SalesPipelineOverview
                title="Service Pipeline Overview"
                subtitle="Enquiry to challan flow for the selected period · hover or click flow dots for path details"
                data={{ stages: serviceKpiState.data.pipeline_stages }}
                onStageClick={(stageId) => handleCardClick("pipeline_" + stageId, "service")}
              />
            ) : serviceKpiState.isLoading ? (
              <SectionSkeleton title="Service Pipeline Overview" sectionId="pipeline" rows={1} columns={5} />
            ) : null}
            {servicePerformanceState.data ? (
              <PerformanceOverview
                title="Performance Overview"
                subtitle="Team and individual service performance"
                data={servicePerformanceState.data}
              />
            ) : servicePerformanceState.isLoading ? (
              <SectionSkeleton title="Performance Overview" sectionId="performance" rows={5} columns={4} />
            ) : null}
            <Box ref={serviceInventoryGate.ref} sx={{ minHeight: serviceInventoryState.data ? 0 : 24 }}>
              {serviceInventoryState.data ? (
                <InventorySnapshot
                  variant="service"
                  data={serviceInventoryState.data}
                  onCardClick={handleInventoryCardClick}
                />
              ) : serviceInventoryState.isLoading || !serviceInventoryGate.shouldRequest ? (
                <SectionSkeleton title="Inventory Snapshot" sectionId="inventory" rows={3} columns={3} />
              ) : null}
            </Box>
            <DashboardKpiGrid
              title="Key Operational Insights"
              subtitle="Operational health metrics for the selected period"
              items={serviceOperationalInsightsItems}
              columns={{ xs: 1, sm: 2, md: 4, lg: 4 }}
              isLoading={serviceInsightsState.isLoading}
              onCardClick={(itemId) => handleCardClick(itemId, "service")}
              sectionId="insights"
            />
            <Box ref={serviceValueCustomersGate.ref} sx={{ minHeight: serviceValueCustomersState.data ? 0 : 24 }}>
              {serviceValueCustomersState.data ? (
                <LowValueCustomer data={serviceValueCustomersState.data} />
              ) : serviceValueCustomersState.isLoading || !serviceValueCustomersGate.shouldRequest ? (
                <SectionSkeleton title="Customer Value" sectionId="low-value" rows={5} columns={5} />
              ) : null}
            </Box>
          </>
        ) : null}

        {activeTab === "sales" && activeView === "timely_comparison" ? (
          <>
            {salesTimelyChartsState.isLoading ? (
              <SectionSkeleton title="Timely Comparison" sectionId="timely" rows={4} columns={2} />
            ) : salesTimelyChartsState.error ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {salesTimelyChartsState.error}
              </Alert>
            ) : salesTimelyChartSeries.length ? (
              <TimelyComparisonCharts
                period={salesComparisonPeriod}
                series={salesTimelyChartSeries}
              />
            ) : null}

            <Box ref={salesTimelyIndividualGate.ref} sx={{ minHeight: 1 }} />
            <DashboardKpiGrid
              title="Sales KPIs"
              subtitle="Product sales pipeline metrics for the selected period"
              items={salesKpiItems}
              columns={{ xs: 1, sm: 2, md: 5, lg: 5 }}
              onCardClick={(itemId) => handleCardClick(itemId, "product")}
              isLoading={salesKpiState.isLoading && !salesKpiState.data}
              sectionId="kpi"
            />
            {salesKpiState.data?.pipeline_stages ? (
              <SalesPipelineOverview
                title="Sales Pipeline Overview"
                subtitle="Enquiry to sales order dispatch flow for the selected period A hover or click flow dots for path details"
                data={{ stages: salesKpiState.data.pipeline_stages }}
                onStageClick={(stageId) => handleCardClick("pipeline_" + stageId, "product")}
              />
            ) : salesKpiState.isLoading ? (
              <SectionSkeleton title="Sales Pipeline Overview" sectionId="pipeline" rows={1} columns={5} />
            ) : null}
            {salesPerformanceState.data ? (
              <PerformanceOverview
                title="Performance Overview"
                subtitle="Team and individual product sales performance"
                data={salesPerformanceState.data}
              />
            ) : salesPerformanceState.isLoading ? (
              <SectionSkeleton title="Performance Overview" sectionId="performance" rows={5} columns={4} />
            ) : null}
            <Box ref={salesInventoryGate.ref} sx={{ minHeight: salesInventoryState.data ? 0 : 24 }}>
              {salesInventoryState.data ? (
                <InventorySnapshot
                  variant="sales"
                  data={salesInventoryState.data}
                  onCardClick={handleInventoryCardClick}
                />
              ) : salesInventoryState.isLoading || !salesInventoryGate.shouldRequest ? (
                <SectionSkeleton title="Inventory Snapshot" sectionId="inventory" rows={3} columns={3} />
              ) : null}
            </Box>
            <DashboardKpiGrid
              title="Key Operational Insights"
              subtitle="Operational health metrics for the selected period"
              items={salesOperationalInsightsItems}
              columns={{ xs: 1, sm: 2, md: 4, lg: 4 }}
              isLoading={salesInsightsState.isLoading && !salesInsightsState.data}
              onCardClick={(itemId) => handleCardClick(itemId, "product")}
              sectionId="insights"
            />
            <Box ref={salesLowValueGate.ref} sx={{ minHeight: salesLowValueCustomersState.data ? 0 : 24 }}>
              {salesLowValueCustomersState.data ? (
                <LowValueCustomer data={salesLowValueCustomersState.data} />
              ) : salesLowValueCustomersState.isLoading || !salesLowValueGate.shouldRequest ? (
                <SectionSkeleton title="Low Value Customer" sectionId="low-value" rows={5} columns={5} />
              ) : null}
            </Box>
          </>
        ) : null}

        {activeTab === "service" && activeView === "timely_comparison" ? (
          <>
            {serviceTimelyChartsState.isLoading ? (
              <SectionSkeleton title="Timely Comparison" sectionId="timely" rows={4} columns={2} />
            ) : serviceTimelyChartsState.error ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                {serviceTimelyChartsState.error}
              </Alert>
            ) : serviceTimelyChartSeries.length ? (
              <TimelyComparisonCharts
                period={serviceComparisonPeriod}
                series={serviceTimelyChartSeries}
              />
            ) : null}

            <Box ref={serviceTimelyIndividualGate.ref} sx={{ minHeight: 1 }} />
            <DashboardKpiGrid
              title="Service KPIs"
              subtitle="Service pipeline metrics for the selected period"
              items={serviceKpiItems}
              columns={{ xs: 1, sm: 2, md: 5, lg: 5 }}
              onCardClick={(itemId) => handleCardClick(itemId, "service")}
              isLoading={serviceKpiState.isLoading && !serviceKpiState.data}
              sectionId="kpi"
            />
            {serviceKpiState.data?.pipeline_stages ? (
              <SalesPipelineOverview
                title="Service Pipeline Overview"
                subtitle="Enquiry to challan flow for the selected period A hover or click flow dots for path details"
                data={{ stages: serviceKpiState.data.pipeline_stages }}
                onStageClick={(stageId) => handleCardClick("pipeline_" + stageId, "service")}
              />
            ) : serviceKpiState.isLoading ? (
              <SectionSkeleton title="Service Pipeline Overview" sectionId="pipeline" rows={1} columns={5} />
            ) : null}
            {servicePerformanceState.data ? (
              <PerformanceOverview
                title="Performance Overview"
                subtitle="Team and individual service performance"
                data={servicePerformanceState.data}
              />
            ) : servicePerformanceState.isLoading ? (
              <SectionSkeleton title="Performance Overview" sectionId="performance" rows={5} columns={4} />
            ) : null}
            <Box ref={serviceInventoryGate.ref} sx={{ minHeight: serviceInventoryState.data ? 0 : 24 }}>
              {serviceInventoryState.data ? (
                <InventorySnapshot
                  variant="service"
                  data={serviceInventoryState.data}
                  onCardClick={handleInventoryCardClick}
                />
              ) : serviceInventoryState.isLoading || !serviceInventoryGate.shouldRequest ? (
                <SectionSkeleton title="Inventory Snapshot" sectionId="inventory" rows={3} columns={3} />
              ) : null}
            </Box>
            <DashboardKpiGrid
              title="Key Operational Insights"
              subtitle="Operational health metrics for the selected period"
              items={serviceOperationalInsightsItems}
              columns={{ xs: 1, sm: 2, md: 4, lg: 4 }}
              isLoading={serviceInsightsState.isLoading && !serviceInsightsState.data}
              onCardClick={(itemId) => handleCardClick(itemId, "service")}
              sectionId="insights"
            />
            <Box ref={serviceValueCustomersGate.ref} sx={{ minHeight: serviceValueCustomersState.data ? 0 : 24 }}>
              {serviceValueCustomersState.data ? (
                <LowValueCustomer data={serviceValueCustomersState.data} />
              ) : serviceValueCustomersState.isLoading || !serviceValueCustomersGate.shouldRequest ? (
                <SectionSkeleton title="Customer Value" sectionId="low-value" rows={5} columns={5} />
              ) : null}
            </Box>
          </>
        ) : null}
        </DashboardSectionMotionProvider>
      </div>
      <DashboardEnquirySidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        type={sidebarType}
        dateRange={sidebarDateRange}
        kpiId={sidebarKpiId}
      />
      <DashboardQuotationSidebar
        open={quotationSidebarOpen}
        onClose={() => setQuotationSidebarOpen(false)}
        type={quotationSidebarType}
        dateRange={quotationSidebarDateRange}
        kpiId={sidebarKpiId}
      />
      <DashboardSalesOrderSidebar
        open={salesOrderSidebarOpen}
        onClose={() => setSalesOrderSidebarOpen(false)}
        type={salesOrderSidebarType}
        dateRange={salesOrderSidebarDateRange}
        kpiId={sidebarKpiId}
      />
      <DashboardPurchaseOrderSidebar
        open={purchaseOrderSidebarOpen}
        onClose={() => setPurchaseOrderSidebarOpen(false)}
        type={purchaseOrderSidebarType}
        dateRange={purchaseOrderSidebarDateRange}
        kpiId={sidebarKpiId}
      />
      <DashboardChallanSidebar
        open={challanSidebarOpen}
        onClose={() => setChallanSidebarOpen(false)}
        type={challanSidebarType}
        dateRange={challanSidebarDateRange}
        kpiId={sidebarKpiId}
      />
      <DashboardInventorySidebar
        open={inventorySidebarOpen}
        onClose={() => setInventorySidebarOpen(false)}
        stockType={inventoryStockType}
        scope={activeTab === "service" ? "accessory" : "all"}
        dateRange={
          (activeTab === "service"
            ? serviceInventoryState.data?.date_range
            : salesInventoryState.data?.date_range) || null
        }
      />
      <DashboardCustomerSidebar
        open={customerSidebarOpen}
        onClose={() => setCustomerSidebarOpen(false)}
        type={customerSidebarType}
        dateRange={customerSidebarDateRange}
        kpiId={customerSidebarKpiId}
      />
    </div>
  );
}




















